import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { FormEvent } from 'react';
import {
  DEVICE_CHANGE_PROCESS_TYPES,
  NUMBER_CHANGE_TYPES,
  TOPUP_KIND_META,
  TOPUP_METHODS,
  TOPUP_PAYMENT_CODES,
  deviceChangeSchema,
  numberChangeSchema,
  topupMethodLabel,
} from '@app/shared';
import type {
  CardPaymentInput,
  ChargeQuoteDto,
  CustomerDto,
  TopupKind,
  TopupMethod,
  TopupOptionDto,
  TopupPurchaseDto,
} from '@app/shared';
import { DataTable } from '../../components/DataTable';
import type { Column } from '../../components/DataTable';
import { LoginIdField } from '../../components/LoginIdSuggest';
import { useNotify } from '../../components/NotifyProvider';
import { ErrorState, LoadingState } from '../../components/PageState';
import { ApiError, api } from '../../lib/api';
import { formatCents, formatDateTime } from '../../lib/format';
import { useFeatureReleased } from '../../release';
import { useAuth } from '../auth/AuthProvider';
import { CardField, useCardsAvailable } from '../billing/CardField';
import type { CardFieldHandle } from '../billing/CardField';
import { useChangeReasonOptions } from '../inventory/api';
import { useCustomerContext } from './CustomerLayout';
import { Section } from './parts';

/**
 * The line changes of the Quick Links menu: Change Mobile number (PC90) and Change Device
 * ID (PC101), which are forms of the Modify page, and the pages Add Topup (PC60, PC64) and
 * Plan Change (PC77).
 */

type Problems = Record<string, string>;
const fromServer = (err: unknown): Problems =>
  err instanceof ApiError ? Object.fromEntries(err.details.map((d) => [d.path, d.message])) : {};
const fromZod = (issues: Array<{ path: Array<string | number>; message: string }>): Problems => {
  const found: Problems = {};
  for (const issue of issues) found[String(issue.path[0])] ??= issue.message;
  return found;
};

/** Refreshes everything that shows the customer after a change. */
function useRefresh(...alsoInvalidate: string[]) {
  const queryClient = useQueryClient();
  return () =>
    Promise.all(
      ['customers', ...alsoInvalidate].map((key) =>
        queryClient.invalidateQueries({ queryKey: [key] }),
      ),
    );
}

const GRID = {
  display: 'grid',
  gap: 2,
  gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
  alignItems: 'start',
} as const;

/* ---------------------------- Change Mobile number -------------------------- */

/** Old MDN (read-only), ZIP Code*, Type of Change*. */
export function NumberChangeForm({ customer }: { customer: CustomerDto }) {
  const notify = useNotify();
  const refresh = useRefresh();
  const [zip, setZip] = useState('');
  const [changeType, setChangeType] = useState<string>(NUMBER_CHANGE_TYPES[0]);
  const [problems, setProblems] = useState<Problems>({});
  const change = useMutation({
    mutationFn: (input: unknown) =>
      api.post<CustomerDto>(`/customers/${customer.id}/number-change`, input),
    onSuccess: refresh,
  });

  if (!customer.line.mdn)
    return <Alert severity="info">This account has no telephone number to change.</Alert>;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const parsed = numberChangeSchema.safeParse({ zip, changeType });
    if (!parsed.success) return setProblems(fromZod(parsed.error.issues));
    try {
      const saved = await change.mutateAsync(parsed.data);
      notify.success(`Mobile number changed to ${saved.line.mdn ?? ''}.`);
      setZip('');
      setProblems({});
    } catch (err) {
      const found = fromServer(err);
      if (Object.keys(found).length) setProblems(found);
      else notify.error(err);
    }
  };

  return (
    <Box
      component="form"
      noValidate
      aria-label="Change Mobile number"
      onSubmit={(e) => void submit(e)}
    >
      <Box sx={{ ...GRID, mb: 2 }}>
        <TextField
          label="Old MDN"
          value={customer.line.mdn}
          slotProps={{ input: { readOnly: true } }}
        />
        <TextField
          required
          label="ZIP Code"
          value={zip}
          onChange={(e) => {
            setZip(e.target.value);
            setProblems((p) => ({ ...p, zip: '' }));
          }}
          error={Boolean(problems.zip)}
          helperText={problems.zip || 'The carrier issues a number for this ZIP code.'}
          slotProps={{ htmlInput: { maxLength: 5, inputMode: 'numeric' } }}
        />
        <TextField
          select
          required
          label="Type of Change"
          value={changeType}
          onChange={(e) => setChangeType(e.target.value)}
          error={Boolean(problems.changeType)}
          helperText={problems.changeType}
        >
          {NUMBER_CHANGE_TYPES.map((t) => (
            <MenuItem key={t} value={t}>
              {t}
            </MenuItem>
          ))}
        </TextField>
      </Box>
      <Button type="submit" variant="contained" disabled={change.isPending}>
        Submit
      </Button>
    </Box>
  );
}

/* ------------------------------ Change Device ID ---------------------------- */

/** Old Device ID (read-only), New Device ID*, Reason*. */
export function DeviceChangeForm({ customer }: { customer: CustomerDto }) {
  const notify = useNotify();
  const refresh = useRefresh('inventory');
  const reasons = useChangeReasonOptions();
  const [newDeviceId, setNewDeviceId] = useState('');
  const [reasonId, setReasonId] = useState('');
  const [returnedTo, setReturnedTo] = useState('');
  const [badReason, setBadReason] = useState('');
  const [problems, setProblems] = useState<Problems>({});
  const featureOn = useFeatureReleased();
  const change = useMutation({
    mutationFn: (input: unknown) =>
      api.post<CustomerDto>(`/customers/${customer.id}/device-change`, input),
    onSuccess: refresh,
  });
  const offered = (reasons.data ?? []).filter((r) =>
    (DEVICE_CHANGE_PROCESS_TYPES as readonly string[]).includes(r.processType),
  );
  // "Reason For Bad Device" is asked when the reason marks the old device Bad (switch bad-reasons).
  const askBad =
    featureOn('bad-reasons') && offered.find((r) => r.id === reasonId)?.reasonStatus === 'Bad';

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const parsed = deviceChangeSchema.safeParse({
      newDeviceId,
      reasonId,
      returnedTo: featureOn('who-got-it-back') ? returnedTo : '',
      badReason: askBad ? badReason : '',
    });
    if (!parsed.success) return setProblems(fromZod(parsed.error.issues));
    if (askBad && !parsed.data.badReason)
      return setProblems({ badReason: 'Enter the Reason For Bad Device' });
    try {
      const saved = await change.mutateAsync(parsed.data);
      notify.success(`Device changed to ${saved.line.imei ?? ''}.`);
      setNewDeviceId('');
      setReasonId('');
      setReturnedTo('');
      setBadReason('');
      setProblems({});
    } catch (err) {
      const found = fromServer(err);
      if (Object.keys(found).length) setProblems(found);
      else notify.error(err);
    }
  };

  return (
    <Box component="form" noValidate aria-label="Change Device ID" onSubmit={(e) => void submit(e)}>
      <Box sx={{ ...GRID, mb: 2 }}>
        <TextField
          label="Old Device ID"
          value={customer.line.imei ?? ''}
          slotProps={{ input: { readOnly: true } }}
        />
        <TextField
          required
          label="New Device ID"
          value={newDeviceId}
          onChange={(e) => {
            setNewDeviceId(e.target.value);
            setProblems((p) => ({ ...p, newDeviceId: '' }));
          }}
          error={Boolean(problems.newDeviceId)}
          helperText={problems.newDeviceId || 'An Available device of your stock.'}
          slotProps={{ htmlInput: { maxLength: 16, inputMode: 'numeric' } }}
        />
        <TextField
          select
          required
          label="Reason"
          value={reasonId}
          onChange={(e) => {
            setReasonId(e.target.value);
            setProblems((p) => ({ ...p, reasonId: '' }));
          }}
          error={Boolean(problems.reasonId)}
          helperText={
            problems.reasonId ||
            (reasons.isError
              ? 'Could not load the reasons'
              : 'Decides what becomes of the old device.')
          }
        >
          {offered.map((r) => (
            <MenuItem key={r.id} value={r.id}>
              {r.name}
            </MenuItem>
          ))}
        </TextField>
        {askBad ? (
          <TextField
            required
            label="Reason For Bad Device"
            value={badReason}
            onChange={(e) => {
              setBadReason(e.target.value);
              setProblems((p) => ({ ...p, badReason: '' }));
            }}
            error={Boolean(problems.badReason)}
            helperText={problems.badReason || undefined}
            placeholder="What is wrong with the old device"
            slotProps={{ htmlInput: { maxLength: 100 } }}
          />
        ) : null}
        {featureOn('who-got-it-back') ? (
          <LoginIdField
            label="Who got it back (Employee Login ID)"
            value={returnedTo}
            onChange={(value) => {
              setReturnedTo(value);
              setProblems((p) => ({ ...p, returnedTo: '' }));
            }}
            error={problems.returnedTo}
          />
        ) : null}
      </Box>
      <Button type="submit" variant="contained" disabled={change.isPending}>
        Submit
      </Button>
    </Box>
  );
}

/* --------------------------------- Add Topup -------------------------------- */

const PURCHASE_COLUMNS: Column<TopupPurchaseDto>[] = [
  { key: 'at', header: 'Date', nowrap: true, render: (p) => formatDateTime(p.at) },
  { key: 'name', header: 'Topup', render: (p) => p.name },
  { key: 'cost', header: 'Amount', align: 'right', render: (p) => formatCents(p.costCents) },
  { key: 'source', header: 'Source', render: (p) => p.source },
  { key: 'by', header: 'Added By', render: (p) => p.by?.loginId ?? '' },
];

/**
 * "Add Minutes / Top-Up Balance" (PC60) and "CSR Screen For Adding Data" (PC64), laid out as
 * the reference (product inventory 13.17): a tab per way of paying with its code, the wallet
 * balance with "Use wallet balance for this purchase", the line, "Top-Up Amount" as
 * "price - code / name", the tax, the processing fee, "Wallet Amount + Pay Amount = Total
 * Amount" and "Complete Purchase". Not offered: Credit Card (PC414, PC420; no gateway) and
 * Dealer Wallet (PC544). The Receipt Number and the list of top-ups added are this project's.
 */
export function TopupPage({ kind }: { kind: TopupKind }) {
  const { customer } = useCustomerContext();
  const { can } = useAuth();
  const meta = TOPUP_KIND_META[kind];
  const notify = useNotify();
  const refresh = useRefresh('billing');
  const cardsAvailable = useCardsAvailable();
  const cardRef = useRef<CardFieldHandle>(null);
  const methods = TOPUP_METHODS.filter(
    (m) => can(TOPUP_PAYMENT_CODES[kind][m]) && (m !== 'card' || cardsAvailable),
  );
  const [method, setMethod] = useState<TopupMethod | ''>(methods[0] ?? '');
  const [topupId, setTopupId] = useState('');
  const [useWallet, setUseWallet] = useState(false);
  const [receiptNumber, setReceiptNumber] = useState('');
  const [problems, setProblems] = useState<Problems>({});
  const screen = useQuery({
    queryKey: ['customers', customer.id, 'topups', kind],
    queryFn: () =>
      api.get<{
        options: TopupOptionDto[];
        purchases: TopupPurchaseDto[];
        walletBalanceCents: number;
      }>(`/customers/${customer.id}/topups/${kind}`),
  });
  // Tax and processing fee depend on the top-up and on the way of paying.
  const quote = useQuery({
    queryKey: ['customers', customer.id, 'topups', kind, 'quote', topupId, method],
    queryFn: () =>
      api.get<ChargeQuoteDto>(`/customers/${customer.id}/topups/${kind}/quote`, {
        topupId,
        method,
      }),
    enabled: Boolean(topupId && method),
    // Keeps the last charge on screen while the next one is asked for.
    placeholderData: (prev) => prev,
  });
  const buy = useMutation({
    mutationFn: (input: unknown) =>
      api.post<{ name: string; costCents: number; quote: ChargeQuoteDto; walletCents: number }>(
        `/customers/${customer.id}/topups/${kind}`,
        input,
      ),
    onSuccess: refresh,
  });

  const wallet = screen.data?.walletBalanceCents ?? 0;
  const total = topupId && quote.data ? quote.data.totalCents : 0;
  const walletBoxOn = useWallet && method !== 'wallet' && method !== 'free' && wallet > 0;
  const walletAmount = method === 'wallet' ? total : walletBoxOn ? Math.min(wallet, total) : 0;
  const payAmount = total - walletAmount;
  const needsReceipt = method === 'cash' || method === 'moneyOrder' || method === 'moneyGram';

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!method) return setProblems({ method: 'Select Payment Method' });
    if (!topupId) return setProblems({ topupId: 'Select Topup' });
    let card: CardPaymentInput | undefined;
    if (method === 'card') {
      try {
        card = await cardRef.current?.collect();
      } catch (err) {
        return setProblems({ card: (err as Error).message });
      }
    }
    try {
      const done = await buy.mutateAsync({
        topupId,
        method,
        receiptNumber: needsReceipt ? receiptNumber : '',
        useWallet: walletBoxOn,
        card,
      });
      notify.success(
        `${done.name} added. ` +
          (done.walletCents
            ? `Wallet ${formatCents(done.walletCents)} + ${topupMethodLabel(method)} ${formatCents(done.quote.totalCents - done.walletCents)}.`
            : `${topupMethodLabel(method)} ${formatCents(done.quote.totalCents)}.`),
      );
      setTopupId('');
      setReceiptNumber('');
      setUseWallet(false);
      setProblems({});
    } catch (err) {
      const found = fromServer(err);
      if (Object.keys(found).length) setProblems(found);
      else notify.error(err);
    }
  };

  const ro = (label: string, value: string) => (
    <TextField label={label} value={value} slotProps={{ input: { readOnly: true } }} />
  );
  return (
    <Section title={`${meta.heading} (${meta.code})`}>
      <Typography color="text.secondary" sx={{ fontSize: 13, mb: 1.5 }}>
        {meta.subtitle}
      </Typography>
      {screen.error ? (
        <ErrorState error={screen.error} onRetry={() => void screen.refetch()} />
      ) : !screen.data ? (
        <LoadingState />
      ) : (
        <>
          {customer.status !== 'Active' ? (
            <Alert severity="info" sx={{ mb: 1.5 }}>
              A top-up can be added to an Active account only. This account is {customer.status}.
            </Alert>
          ) : methods.length === 0 ? (
            <Alert severity="info" sx={{ mb: 1.5 }}>
              No way of paying is open to you on this screen.
            </Alert>
          ) : screen.data.options.length === 0 ? (
            <Alert severity="info" sx={{ mb: 1.5 }}>
              No top-up of this kind is offered for this line (carrier{' '}
              {customer.line.carrier?.code ?? 'unknown'}, state {customer.serviceAddress.state}).
            </Alert>
          ) : (
            <Box
              component="form"
              noValidate
              aria-label={meta.heading}
              onSubmit={(e) => void submit(e)}
              sx={{ mb: 2 }}
            >
              <Tabs
                value={method || false}
                onChange={(_e, m: TopupMethod) => {
                  setMethod(m);
                  setProblems({});
                }}
                aria-label="Payment Method"
                sx={{ mb: 1.5, borderBottom: 1, borderColor: 'divider' }}
              >
                {methods.map((m) => (
                  <Tab
                    key={m}
                    value={m}
                    label={`${topupMethodLabel(m)} (${TOPUP_PAYMENT_CODES[kind][m]})`}
                  />
                ))}
              </Tabs>
              {problems.method ? (
                <Alert severity="error" sx={{ mb: 1.5 }}>
                  {problems.method}
                </Alert>
              ) : null}
              <Box sx={{ ...GRID, mb: 2 }}>
                <Box>
                  {ro('Available Wallet Balance: $', (wallet / 100).toFixed(2))}
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={walletBoxOn}
                        disabled={wallet <= 0 || method === 'wallet' || method === 'free'}
                        onChange={(e) => setUseWallet(e.target.checked)}
                      />
                    }
                    label={meta.walletBox}
                  />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: 13 }} color="text.secondary">
                    Phone Number(s):
                  </Typography>
                  <FormControlLabel
                    control={<Checkbox checked disabled />}
                    label={customer.line.mdn ?? ''}
                  />
                </Box>
                <TextField
                  select
                  required
                  label="Top-Up Amount:"
                  value={topupId}
                  onChange={(e) => {
                    setTopupId(e.target.value);
                    setProblems({});
                  }}
                  error={Boolean(problems.topupId)}
                  helperText={
                    problems.topupId ||
                    (can('PC232') ? (
                      <Link to="/settings/plans?tab=topup">{meta.addLink}</Link>
                    ) : undefined)
                  }
                >
                  <MenuItem value="" disabled>
                    {meta.select}
                  </MenuItem>
                  {screen.data.options.map((o) => (
                    <MenuItem key={o.id} value={o.id}>
                      {(o.costCents / 100).toFixed(2).replace(/\.00$/, '')} - {o.code || '-'} /{' '}
                      {o.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
              {topupId && quote.data ? (
                <Box sx={{ ...GRID, mb: 2 }} aria-label="Amounts">
                  {ro(
                    'Tax Breakdown:',
                    quote.data.taxRatePercent ? `${quote.data.taxRatePercent}%` : 'No tax',
                  )}
                  {ro('Total Tax:', (quote.data.taxCents / 100).toFixed(2))}
                  {ro('Processing Fee: $', (quote.data.processingFeeCents / 100).toFixed(2))}
                </Box>
              ) : null}
              <Typography variant="subtitle2" component="h4">
                Total Amount:
              </Typography>
              <Box
                aria-label="Total Amount"
                sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}
              >
                {ro('Wallet Amount', (walletAmount / 100).toFixed(2))}
                <Typography>+</Typography>
                {ro('Pay Amount', (payAmount / 100).toFixed(2))}
                <Typography>=</Typography>
                {ro('Total Amount', (total / 100).toFixed(2))}
              </Box>
              {needsReceipt ? (
                <TextField
                  label="Receipt Number"
                  value={receiptNumber}
                  onChange={(e) => setReceiptNumber(e.target.value)}
                  slotProps={{ htmlInput: { maxLength: 30 } }}
                  sx={{ mb: 2, width: 260 }}
                />
              ) : null}
              {method === 'card' ? (
                <Box sx={{ mb: 2, maxWidth: 480 }}>
                  <CardField
                    ref={cardRef}
                    customerId={customer.id}
                    error={problems.card ?? problems['card.savedCardId']}
                  />
                </Box>
              ) : null}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Button type="submit" variant="contained" disabled={buy.isPending}>
                  Complete Purchase
                </Button>
                <Typography color="text.secondary" sx={{ fontSize: 12 }}>
                  {meta.note}
                </Typography>
              </Box>
            </Box>
          )}
          <Typography variant="h3" sx={{ mb: 1 }}>
            Top-ups added to this line
          </Typography>
          <DataTable
            label="Top-ups added to this line"
            columns={PURCHASE_COLUMNS}
            rows={screen.data.purchases}
            rowKey={(p) => p.id}
            emptyText="No top-up has been added to this line."
          />
        </>
      )}
    </Section>
  );
}
